'use client';

import { useState, type ReactNode } from 'react';
import { LessonCalendar } from './lesson-calendar';
import { isUpcomingStudentBooking, studentBookingTimeLabel } from '@/lib/student-booking-time';

type RecordBooking = {
  guestCount?: number; upgradeCharge?: { amount: number; status: string } | null;
  id: string; courseName: string; startsAt?: string; endsAt?: string; startsAtLabel: string;
  status: string; teacherName: string; venueName: string; roomName: string | null; reviewStatus?: string | null;
};

function recordStatus(booking: RecordBooking, now: number) {
  const timed = studentBookingTimeLabel(booking, now);
  if (timed) return { label: timed, kind: timed === '待核銷' ? 'pending' : 'booked' };
  if (booking.reviewStatus === 'PENDING') return { label: '取消待判定', kind: 'cancelled' };
  if (booking.status === 'CANCELLED') return { label: '已取消', kind: 'cancelled' };
  if (booking.status === 'NO_SHOW') return { label: '缺席', kind: 'absent' };
  if (['COMPLETED', 'CHECKED_IN'].includes(booking.status)) return { label: '已完成', kind: 'attended' };
  return { label: '已預約', kind: 'booked' };
}

export function StudentRecords<T extends RecordBooking>({ bookings, now, timezone, renderActions }: {
  bookings: T[]; now: number; timezone: string; renderActions: (booking: T) => ReactNode;
}) {
  const dayFormat = new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' });
  const today = dayFormat.format(new Date(now));
  const [month, setMonth] = useState(() => today.slice(0, 7));
  const [day, setDay] = useState('');
  const [view, setView] = useState<'calendar' | 'all'>('calendar');
  const dayKey = (booking: T) => booking.startsAt && Number.isFinite(Date.parse(booking.startsAt)) ? dayFormat.format(new Date(booking.startsAt)) : '';
  const sorted = [...bookings].sort((a, b) => (Date.parse(b.startsAt ?? '') || 0) - (Date.parse(a.startsAt ?? '') || 0));
  const shown = view === 'all' ? sorted : sorted.filter(booking => day ? dayKey(booking) === day : dayKey(booking).startsWith(month));
  const timeFormat = new Intl.DateTimeFormat('zh-TW', { timeZone: timezone, hour: '2-digit', minute: '2-digit', hour12: false });
  function moveMonth(offset: number) {
    const [year, number] = month.split('-').map(Number);
    setMonth(new Date(Date.UTC(year!, number! - 1 + offset, 1)).toISOString().slice(0, 7));
    setDay('');
  }
  return <section className="student-records">
    <header className="student-records-heading"><div><h2>我的課表</h2><p>查看預約、上課時間與出席紀錄</p></div></header>
    <div className="student-records-tabs" role="group" aria-label="課表顯示方式">
      <button type="button" aria-pressed={view === 'calendar'} onClick={() => setView('calendar')}>月曆課表</button>
      <button type="button" aria-pressed={view === 'all'} onClick={() => setView('all')}>全部紀錄 <span>{bookings.length}</span></button>
    </div>
    {view === 'calendar' && <div className="student-records-calendar">
      <LessonCalendar month={month} day={day} today={today} onMonthChange={moveMonth} onDayChange={setDay}
        records={bookings.map(booking => ({ day: dayKey(booking), ...recordStatus(booking, now) }))} />
      <div className="student-calendar-help"><span>點選日期查看當天課程</span><button type="button" onClick={() => { setMonth(today.slice(0, 7)); setDay(today); }}>今天</button></div>
    </div>}
    <div className="student-records-list-heading"><h3>{view === 'all' ? '全部預約與上課紀錄' : day ? `${day.replaceAll('-', '/')} 的課程` : `${month.replace('-', '/')} 的課程`}</h3><span>{shown.length} 堂</span>{day && view === 'calendar' && <button type="button" onClick={() => setDay('')}>顯示整月</button>}</div>
    <div className="student-records-list">
      {shown.map(booking => {
        const date = dayKey(booking) ? new Date(booking.startsAt!) : null;
        const state = recordStatus(booking, now);
        return <article className="student-record-row" key={booking.id}>
          <div className="student-record-date">{date ? <><span>{date.toLocaleDateString('zh-TW', { timeZone: timezone, year: 'numeric', month: '2-digit' })}</span><strong>{new Intl.DateTimeFormat('en', { timeZone: timezone, day: '2-digit' }).format(date)}</strong><small>{date.toLocaleDateString('zh-TW', { timeZone: timezone, weekday: 'short' })}</small></> : <span>日期待確認</span>}</div>
          <div className="student-record-body">
            <div className="student-record-topline"><strong className="student-record-time">{date ? timeFormat.format(date) : booking.startsAtLabel}{booking.endsAt && <small>–{timeFormat.format(new Date(booking.endsAt))}</small>}</strong><span className={`student-record-status ${state.kind}`}>{state.label}</span></div>
            <h3>{booking.courseName}</h3>{Boolean(booking.guestCount) && <p>自行攜伴 {booking.guestCount} 人</p>}{booking.upgradeCharge && <p>升級加價 NT${(booking.upgradeCharge.amount / 100).toLocaleString('zh-TW')} · {({UNPAID:'待現場付款',PAID:'已收款',REFUND_PENDING:'待退款',REFUNDED:'已退款',WAIVED:'已免收'} as Record<string,string>)[booking.upgradeCharge.status] ?? booking.upgradeCharge.status}</p>}
            <p>{booking.teacherName.endsWith('老師') ? booking.teacherName : `${booking.teacherName}老師`} · {booking.venueName}{booking.roomName ? ` · ${booking.roomName}` : ''}</p>
            {booking.reviewStatus && <p>{booking.reviewStatus === 'PENDING' ? '堂數仍保留，等待老師判定' : booking.reviewStatus === 'REFUND' ? '已回補堂數' : '已判定扣堂'}</p>}
            {isUpcomingStudentBooking(booking, now) && renderActions(booking)}
          </div>
        </article>;
      })}
      {!shown.length && <div className="student-records-empty"><strong>{view === 'all' ? '目前還沒有預約紀錄' : '這段時間沒有安排課程'}</strong><p>{view === 'calendar' ? '切換月份，或查看全部紀錄。' : '完成預約後，課程會顯示在這裡。'}</p></div>}
    </div>
  </section>;
}
